import Vue from "vue";
import Vuex from "vuex";
import {getActiveVoucher} from '@/utils/getActiveVoucher';
import _ from 'lodash';
import auth from './auth'
import app from './app'
import {createAuthAttributes} from '@/utils/auth/createAuthAttributes';
import {fetchOnce} from '@/store/_utils';
import {isNonEmptyString} from '@/utils';

Vue.use(Vuex);

let base_url
if (process.env.BASE_API_URL) {
  base_url = process.env.BASE_API_URL
} else if (process.env.TARGET === 'staging') {
  base_url = "https://staging.helloagain.at"
} else if (process.env.TARGET === 'playground') {
  base_url = "https://playground.helloagain.at"
} else {
  base_url = "https://api.helloagain.at"
}

export const BASE_API_URL = base_url;
export const API_CLIENT = process.env.API_CLIENT

export const IS_DEV = process.env.NODE_ENV === 'development';
export let storage;

export function setStorage(prefixedStorage) {
  storage = prefixedStorage;
}

console.log('BASE_API_URL', BASE_API_URL);

// export const storage = getStorage();

export default new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  modules: {
    app,
    auth
  },
  state: {
    client_id: null,
    client: null,
    user: null,
    locale: 'de',
    customer_relationship: null,
    rewards: [],
    sweepstakes: [],
    groupedRewards: {},
    rewardsAppliedFilters: [],
    activities: [],
    tosScreen: {show: false, acceptAction: null},
    activeVoucherDialog: {show: false, bounty: null},
    showFriendInviteRedeemDialog: false,
    device: null,
    fetched: {
      rewards: false,
      bannerSlides: false
    },
    clientSlug: null,
    firebaseConfig: {},
    webAppConfig: {},
    customTranslations: {},
    userIdentifierForVerification: '',
    firebaseResult: null
  },
  mutations: {
    setUser(state, payload) {
      state.user = payload;
    },
    setUserIdentifierForVerification(state, identifier) {
      state.userIdentifierForVerification = identifier
    },
    setFirebaseResult(state, identifier) {
      state.firebaseResult = identifier
    },
    setCustomerRelationship(state, payload) {
      state.customer_relationship = payload;
      state.user = {
        ...payload.appuser,
        customer_id: _.get(payload, 'customer_id', null)
      }
    },
    setRewards(state, payload) {
      state.rewards = payload.in_shop;
      state.sweepstakes = payload.contest;
    },
    setGroupedRewards(state, payload) {
      const group = _.isEmpty(payload.group)
        ? 'all'
        : payload.group
      Vue.set(state.groupedRewards, group, payload.rewards)
    },
    setActivities(state, payload) {
      state.activities = payload;
    },
    setClient(state, payload) {
      state.client = payload;
      document.title = state.client.name;
    },
    setTOSScreen(state, payload) {
      state.tosScreen = payload;
    },
    setTOSVersion(state, payload) {
      state.customer_relationship.appuser.acceptCurrentTOS = payload;
    },
    setActiveVoucherDialog(state, payload) {
      state.activeVoucherDialog = payload;
    },
    setFriendInviteRedeemDialog(state, payload) {
      state.showFriendInviteRedeemDialog = payload;
    },
    SET_DEVICE(state, payload) {
      state.device = payload
    },
    SET_LOCALE(state, locale) {
      state.locale = locale
    },
    SET_FETCHED(state, key) {
      _.set(state.fetched, key, true);
    },
    SET_CLIENT_ID(state, id) {
      state.client_id = id
    },
    SET_CLIENT_SLUG(state, slug) {
      state.clientSlug = slug;
    },
    SET_FIREBASE_CONFIG(state, config) {
      state.firebaseConfig = config;
    },
    SET_WEB_APP_CONFIG(state, config) {
      state.webAppConfig = config;
    },
    setCustomTranslations(state, payload) {
      state.customTranslations = payload;
    },
    clearRewardsAppliedFilters(state) {
      state.rewardsAppliedFilters = []
    },
    addRewardsAppliedFilter(state, filterValue) {
      state.rewardsAppliedFilters.push(filterValue)
    },
    deleteRewardsAppliedFilter(state, filterValue) {
      state.rewardsAppliedFilters = state.rewardsAppliedFilters.filter((filter) => {
        return filter !== filterValue
      })
    }
  },
  actions: {
    async fetchUserProfile({commit}) {
      try {
        const response = await Vue.axios.get(`${BASE_API_URL}/api/v1/users/profile/`);
        commit('setUser', response.data);
      } catch (error) {
        console.log('Error fetching user profile', error);
      }
    },
    async updateUserProfile({state, dispatch}, payload) {
      try {
        const response = await Vue.axios.patch(
          `${BASE_API_URL}/api/v1/users/profile/?client_id=${state.client_id}`,
          payload
        )
        const userPayload = {
          ...state.user,
          ...response.data
        }

        await dispatch('updateUserData', userPayload)
        await dispatch('showFriendsInviteRedeemDialog')

        return response.data
      } catch (error) {
        throw error.response.data
      }
    },
    async registerPass(_, payload) {
      try {
        let responseConfig = {}
        if (payload.device === 'ios') {
          responseConfig = {
            responseType: 'blob'
          }
        }
        const response = await Vue.axios.post(
          `/api/v1/wallet_pass/register/`,
          payload,
          responseConfig
        )
        return response.data
      } catch (error) {
        throw error
      }
    },
    async updateUserLocale(_, locale) {
      try {
        const response = await Vue.axios.put(
            `${BASE_API_URL}/api/v1/users/profile/locale/`,
            {
              locale
            }
        )
        return response.data
      } catch (error) {
        throw error.response.data
      }
    },
    async getRewards(context, {next, once}) {
      return fetchOnce(context, once, next, 'rewards', () => {
        return Vue.axios.get(`${BASE_API_URL}/api/v1/clients/${context.state.client_id}/bounties-list/`).then(function(response) {
          context.commit('setRewards', response.data);
        }).then(() => {
          if (next) {
            next()
          }
        }).catch((error) => {
          console.warn("Error fetching subclients: " + error)
        })
      })
    },
    async getRewardsByCategory({state, commit}, payload) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/clients/${state.client_id}/bounties/${payload.query}`
        );

        commit('setGroupedRewards', {
          group: payload.group,
          rewards: response.data
        })
      } catch (error) {
        console.warn("Error fetching rewards by category: " + error);
      }
    },
    async fetchCustomerRelationship({commit, dispatch}) {
      let response = await Vue.axios.get(`${BASE_API_URL}/api/v1/customer-relationships/`);
      commit('setCustomerRelationship', response.data);
      dispatch('fetchActivities');
    },
    async fetchActivities({state, commit}) {
      let response = await Vue.axios.get(`${BASE_API_URL}/api/v1/customer-relationships/${state.client_id}/activity-feed/`);
      commit('setActivities', response.data);
    },
    async fetchClient({state, commit}) {
      let response = await Vue.axios.get(`${BASE_API_URL}/api/v1/clients/${state.client_id}/detail/`);
      commit('setClient', response.data);
    },
    tosScreen({commit}, obj) {
      commit('setTOSScreen', obj);
    },
    async acceptCurrentTOS({state, commit, dispatch}) {
      try {
        await Vue.axios.post(`${BASE_API_URL}/api/v1/users/accept-tos/`);
        commit('setTOSVersion', state.app.extensionSettings.auth.current_tos_version);
        dispatch('tosScreen', {show: false});
      } catch (error) {
        console.error('error getting curret_tos_version', error)
      }
    },
    async fetchRewardsData({dispatch}) {
      dispatch('fetchCustomerRelationship');
    },
    setActiveVoucherDialog({commit}) {
      let activeStorageVoucher = getActiveVoucher();
      if (_.get(activeStorageVoucher, 'bounty')) {
        commit('setActiveVoucherDialog', {show: true, bounty: activeStorageVoucher.bounty});
      }
    },
    async removeActiveVoucherDialog({commit, dispatch}) {
      commit('setActiveVoucherDialog', {show: false, bounty: null});
      await dispatch('fetchRewardsData');
    },
    updateUserData({commit}, obj) {
      commit('setUser', obj);
      commit(
        'SET_VERIFICATION_ATTRIBUTES',
        createAuthAttributes({signupMode: false})
      );
    },
    showFriendsInviteRedeemDialog({dispatch}) {
      const inviteCode = JSON.parse(storage.getItem('inviteCode'))

      if (
        inviteCode &&
        inviteCode.code &&
        new Date().getTime() < inviteCode.exp
      ) {
        dispatch('setFriendInviteRedeem', true)
      } else {
        storage.removeItem('inviteCode')
      }
    },
    setFriendInviteRedeem({commit}, show) {
      commit('setFriendInviteRedeemDialog', show);
    },
    setDevice({commit}, device) {
      commit('SET_DEVICE', device);
    },
    setLocale({commit}, locale) {
      commit('SET_LOCALE', locale);
    },
    setClientId({commit}, id) {
      commit('SET_CLIENT_ID', id);
    },
    setClientSlug({commit}, slug) {
      commit('SET_CLIENT_SLUG', slug);
    },
    async setFirebaseConfig({commit}, {slug, baseUrl}) {
      return fetch(`${baseUrl}/api/v1/clients/${slug}/config/config.js`).then((data) => {
        return data.text()
          .then((response) => {
            commit('SET_WEB_APP_CONFIG', eval(response + 'webAppConfig;'))
            commit('SET_FIREBASE_CONFIG', eval(response + 'firebaseConfig;'))
          })
      })
    },
    async getCustomTranslations({state, commit}) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/bob/web-app-translations/?client_slug=${state.clientSlug}`
        )

        commit('setCustomTranslations', response.data)
      } catch (error) {
        console.error('error getting custom translations', error)
      }
    },
    async fetchPromoBounties() {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/customer-relationships/promo-bounties/`
        )

        return response.data
      } catch (error) {
        console.error('error getting promo bounties', error)
      }
    },
    async fetchActivatedPromoBounties() {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/customer-relationships/activated-promo-bounties/`
        )

        return response.data
      } catch (error) {
        console.error('error getting activated promo bounties', error)
      }
    },
    async redeemShippingReward({state}, payload) {
      try {
        const response = await Vue.axios.post(
          `/api/v1/clients/${state.client_id}/bounties/redeem/`,
          payload
        )

        return response.data
      } catch (error) {
        return {
          error: 'REDEEM_SHIPPING_REWARD_FAILED'
        }
      }
    }
  },
  getters: {
    client(state) {
      return state.client;
    },
    client_id(state) {
      return state.client_id;
    },
    clientSlug(state) {
      return state.clientSlug
    },
    customerRelationship(state) {
      return state.customer_relationship
    },
    user(state) {
      return state.user
    },
    device(state) {
      return state.device
    },
    tosScreenState(state) {
      return state.tosScreen
    },
    firebaseConfig(state) {
      return state.firebaseConfig
    },
    getRewardById: (state) => (id) => {
      let rewards = _.concat(state.rewards, state.sweepstakes);
      return rewards.find((reward) => reward.id === id)
    },
    isEmailAddressChangeable(state, rootGetters) {
      const userCanEditEmail = _.get(state.user, 'can_edit_email', false)
      const userCanSetEmail =
        _.get(rootGetters.authSettings, 'attributes.email.ask_after_login')
          && !isNonEmptyString(_.get(state.user, 'email'))

      return userCanEditEmail || userCanSetEmail
    }
  }
});
